/**
 * use-folder-tree-index.ts — Task 3 of FolderTreeV3
 *
 * Provides three hooks for consuming the flat v2/folder_with_tags API:
 *
 *  useFlatPlantFolders — fetches raw FlatFolderTagRow[] from the API.
 *    Mirrors usePlantFolders (app/hooks/folders.ts:63-132) exactly:
 *    - Same query-key shape: [FLAT_FOLDER_KEY, selectedEnsemble?.family_id]
 *    - Same cacheTime (8 hours) and staleTime (1 hour)
 *    - Same ensemble resolution: view-state ensemble ?? context ensemble
 *    - Disabled unless selectedEnsemble?.family_id is truthy
 *
 *  useFolderTreeIndex — builds and memoizes a FolderTreeIndex from flat rows.
 *    Returns undefined when rows is undefined or empty so callers can gate
 *    rendering on index availability without a non-null assertion.
 *
 *  usePlantFolderIndex — convenience hook combining the two above.
 *    Returns { index, isLoading, error } for one-line consumption.
 */

import { useContext, useMemo } from 'react';
import { useQuery } from 'react-query';

import { EnsembleContext } from '../../app/context/ensemble-context';
import { FolderSearchPayload, getFlatFolders } from '../../app/services/folder';
import { useTabManager } from '../context/tab-manager-context';

import { ApiResult, FlatFolderTagRow } from '@controlrooms/models';
import { buildFolderTreeIndex, FolderTreeIndex } from '@controlrooms/utils';

// ---------------------------------------------------------------------------
// Cache key — exported so consumers and tests can reference it directly
// ---------------------------------------------------------------------------

export const FLAT_FOLDER_KEY = 'FLAT_PLANT_FOLDERS';

// ---------------------------------------------------------------------------
// useFlatPlantFolders
// ---------------------------------------------------------------------------

/**
 * Fetch flat folder+tag rows for the currently active plant.
 *
 * Matches usePlantFolders (folders.ts:63-132) for:
 *  - Ensemble resolution (view-state ensemble takes priority over context)
 *  - Query key shape: [FLAT_FOLDER_KEY, family_id]
 *  - staleTime: 1 hour, cacheTime: 8 hours
 *  - Query is disabled when family_id is absent/falsy
 */
export const useFlatPlantFolders = (plantId = -1) => {
  const { activeViewState } = useTabManager();
  const viewEnsemble = activeViewState?.selectedEnsemble as
    | { family_id?: string }
    | null
    | undefined;
  const { selectedEnsemble: appEnsemble } = useContext(EnsembleContext);

  // View-state ensemble takes priority; fall back to app-level context ensemble
  const selectedEnsemble = viewEnsemble ?? appEnsemble;

  const payload: FolderSearchPayload = { top_folder: plantId };
  if (selectedEnsemble?.family_id) {
    payload.ensemble_family_id = selectedEnsemble.family_id;
  }

  return useQuery([FLAT_FOLDER_KEY, selectedEnsemble?.family_id], () => getFlatFolders(payload), {
    select: (body: ApiResult<FlatFolderTagRow[]>): FlatFolderTagRow[] => body.result,
    enabled: !!selectedEnsemble?.family_id,
    staleTime: 60 * 60 * 1000, // 1 hour — matches usePlantFolders
    cacheTime: 8 * 60 * 60 * 1000, // 8 hours — matches usePlantFolders
  });
};

// ---------------------------------------------------------------------------
// useFolderTreeIndex
// ---------------------------------------------------------------------------

/**
 * Build and memoize a FolderTreeIndex from flat API rows.
 *
 * Returns undefined when rows is undefined or empty so callers can gate
 * rendering on index availability without a non-null assertion.
 * The index is rebuilt only when the `rows` reference changes.
 */
export const useFolderTreeIndex = (rows?: FlatFolderTagRow[]): FolderTreeIndex | undefined => {
  return useMemo(() => {
    if (!rows || rows.length === 0) return undefined;
    return buildFolderTreeIndex(rows);
  }, [rows]);
};

// ---------------------------------------------------------------------------
// usePlantFolderIndex
// ---------------------------------------------------------------------------

/**
 * Combined convenience hook: fetches flat rows and builds the index in one call.
 *
 * Returns:
 *  - `index`     — FolderTreeIndex once data is available, undefined while loading
 *  - `isLoading` — true while the fetch is in flight
 *  - `error`     — the query error if the fetch failed, null otherwise
 */
export const usePlantFolderIndex = () => {
  const { data: rows, isLoading, error } = useFlatPlantFolders();
  const index = useFolderTreeIndex(rows);
  return { index, isLoading, error };
};
